import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { AutoTempModes, CoolingModes, TemperatureControlModes, ThermModes } from '@library/store/homes/homes.interface';
import { SchedulesFacade } from '@library/store/schedules/schedules.facade';
import { isDefined } from '@library/utils/helpers/store/is-defined';
import { SidebarControlModeService } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-sidebar/sidebar-control-mode/sidebar-control-mode.service';
import { HeatingModes } from '@pages/control-page/control-page-store.service';
import { Subscription, combineLatest, firstValueFrom, map, switchMap } from 'rxjs';

@Component({
  selector: 'app-home-mode-schedules',
  templateUrl: './home-mode-schedules.component.html',
  styleUrl: './home-mode-schedules.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class HomeModeSchedulesComponent {

  HeatingModes = HeatingModes;
  TemperatureControlModes = TemperatureControlModes;

  scheduleControl = new FormControl<{value: string, label: string}>(null);

  private currentSchedule$ = this.controlModeService.temperatureControlMode$.pipe(
    switchMap((thermoMode) => {
      switch(thermoMode) {
        case TemperatureControlModes.HEATING:
          return this.schedulesFacade.currentSelectedThermSchedule$;
        case TemperatureControlModes.COOLING:
          return this.schedulesFacade.currentSelectedCoolingSchedule$;
        case TemperatureControlModes.AUTO:
          return this.schedulesFacade.currentSelectedAutoSchedule$;
      }
    }),
    isDefined(),
  );

  private selected$ = combineLatest({
    temperatureControlMode: this.controlModeService.temperatureControlMode$,
    thermMode: this.controlModeService.thermMode$,
    coolingMode: this.controlModeService.coolingMode$,
    autoTempMode: this.controlModeService.autoTempMode$,
  }).pipe(
    map(({coolingMode, autoTempMode, thermMode, temperatureControlMode}) => {
      if (temperatureControlMode === TemperatureControlModes.HEATING) {
        return thermMode === ThermModes.SCHEDULE;
      }
      if (temperatureControlMode === TemperatureControlModes.COOLING) {
        return coolingMode === CoolingModes.SCHEDULE;
      }
      else if (temperatureControlMode === TemperatureControlModes.AUTO) {
        return autoTempMode === AutoTempModes.SCHEDULE;
      }
    }),
  );

  viewModel$ = combineLatest({
    heatingSchedules: this.homesFacade.heatingSchedules$,
    coolingSchedules: this.homesFacade.coolingSchedules$,
    autoSchedules: this.homesFacade.autoSchedules$,
    currentSchedule: this.currentSchedule$,
    selected: this.selected$,
    temperatureControlMode: this.controlModeService.temperatureControlMode$,
  });

  sub = new Subscription();

  constructor(
    private homesFacade: HomesFacade,
    private schedulesFacade: SchedulesFacade,
    private controlModeService: SidebarControlModeService,
  ) {}

  async ngOnInit() {
    this.sub.add(this.homesFacade.currentHomeId$.subscribe(async () => {
      const currentSchedule = await firstValueFrom(this.currentSchedule$);
      this.scheduleControl.setValue({value: currentSchedule.id, label: currentSchedule.name}, {emitEvent: false});
    }));

    // Wait for the value to be set in the form
    // Otherwise the value is changed before we subscribe to `valueChanges`
    // and automatically selects a schedule
    await firstValueFrom(this.currentSchedule$);

    this.sub.add(this.scheduleControl.valueChanges.subscribe(async ({value}) => {
      this.selectSchedule(value);
    }));
  }

  async selectSchedule(scheduleId: string) {
    const schedules = await firstValueFrom(this.schedulesFacade.currentHomeSchedules);
    const schedule = schedules.find(s => s.id === scheduleId);
    const home = await firstValueFrom(this.homesFacade.currentHome$);
    switch (schedule.type) {
      case 'therm':
        this.controlModeService.selectMode$.next(ThermModes.SCHEDULE);
        this.controlModeService.selectTemperatureControlMode$.next(TemperatureControlModes.HEATING);
        await firstValueFrom(this.homesFacade.setHeatingMode(home.id, ThermModes.SCHEDULE, null));
        break;
      case 'cooling':
        this.controlModeService.selectMode$.next(CoolingModes.SCHEDULE);
        this.controlModeService.selectTemperatureControlMode$.next(TemperatureControlModes.COOLING);
        await firstValueFrom(this.homesFacade.setCoolingMode(home.id, CoolingModes.SCHEDULE, null));
        break;
      case 'auto':
        this.controlModeService.selectMode$.next(AutoTempModes.SCHEDULE);
        this.controlModeService.selectTemperatureControlMode$.next(TemperatureControlModes.AUTO);
        await firstValueFrom(this.homesFacade.setAutoMode(home.id, AutoTempModes.SCHEDULE, null));
        break;
    }
    await firstValueFrom(this.schedulesFacade.selectNewSchedule(home.id, schedule.id));
    this.homesFacade.loadHomeStatus({home_id: home.id});
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
